import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BookOpen, Clock, Layers, ArrowLeft, AlertTriangle, ChevronRight, Loader2, Target, Wrench, Search, RefreshCw } from 'lucide-react';
import { listLibraryGuides, getLibraryGuide } from '../services/api';

const CATEGORY_META = {
  'onboarding':   { label: 'Getting Started', icon: '🚀' },
  'verticals':    { label: 'Signature Verticals', icon: '⭐' },
  'memecoin-dex': { label: 'Memecoins & DEX', icon: '🚀' },
  'strategy-dev': { label: 'Strategy Development', icon: '🧪' },
  'risk-mgmt':    { label: 'Risk Management', icon: '🛡️' },
  'community':    { label: 'Community-Powered', icon: '👥' },
};

const DIFF_COLORS = {
  beginner: 'text-emerald-400 bg-emerald-500/10',
  intermediate: 'text-blue-400 bg-blue-500/10',
  advanced: 'text-violet-400 bg-violet-500/10',
  expert: 'text-amber-400 bg-amber-500/10',
};

export default function LearningLibraryPage() {
  const [params, setParams] = useSearchParams();
  const guideId = params.get('guide');
  const [guides, setGuides] = useState([]);
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  // Surface load failures so users can retry instead of seeing an empty page
  // that makes the search appear broken.
  const [loadError, setLoadError] = useState(null);

  function loadGuides() {
    setLoading(true);
    setLoadError(null);
    listLibraryGuides()
      .then(d => setGuides(d.guides || []))
      .catch(err => setLoadError(err?.message || 'Could not load library guides'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadGuides(); }, []);

  useEffect(() => {
    if (guideId) {
      setLoading(true);
      setLoadError(null);
      getLibraryGuide(guideId)
        .then(setGuide)
        .catch(err => { setGuide(null); setLoadError(err?.message || 'Could not load this guide'); })
        .finally(() => setLoading(false));
    } else {
      setGuide(null);
      setLoadError(null);
    }
  }, [guideId]);

  if (loading && !guides.length) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;
  }

  if (guideId && guide) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
        <button onClick={() => setParams({})} className="text-gray-400 hover:text-white text-xs flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to library
        </button>

        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-6">
          <div className="flex items-start gap-4">
            <span className="text-4xl">{guide.iconEmoji}</span>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white">{guide.title}</h1>
              <p className="text-gray-400 text-sm mt-1">{guide.subtitle}</p>
              <div className="flex flex-wrap items-center gap-2 mt-3 text-xs">
                <span className="flex items-center gap-1 text-gray-500"><Clock className="w-3 h-3" /> {guide.estimatedTime}</span>
                <span className={`px-2 py-0.5 rounded font-bold ${DIFF_COLORS[guide.difficulty] || ''}`}>{guide.difficulty}</span>
                <span className="flex items-center gap-1 text-gray-500"><Wrench className="w-3 h-3" /> {guide.featuresUsed?.length || 0} features</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
          <h2 className="text-white font-bold mb-3 flex items-center gap-2"><Target className="w-4 h-4 text-amber-400" /> What you'll learn</h2>
          <ul className="space-y-1.5">{(guide.outcomes || []).map((o, i) => <li key={i} className="text-sm text-gray-300 flex items-start gap-2"><span className="text-emerald-400">✓</span> {o}</li>)}</ul>
        </div>

        {guide.warnings?.length > 0 && (
          <div className="bg-amber-500/5 rounded-2xl border border-amber-500/20 p-5">
            <h2 className="text-amber-400 font-bold mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Important warnings</h2>
            <ul className="space-y-1.5">{guide.warnings.map((w, i) => <li key={i} className="text-sm text-amber-300">• {w}</li>)}</ul>
          </div>
        )}

        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
          <h2 className="text-white font-bold mb-4">The workflow</h2>
          <div className="space-y-4">
            {(guide.steps || []).map(step => (
              <div key={step.stepNumber} className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-amber-500 text-dark-900 font-bold text-sm flex items-center justify-center flex-shrink-0">{step.stepNumber}</div>
                <div className="flex-1">
                  <h3 className="text-white font-bold text-sm">{step.title}</h3>
                  <p className="text-gray-300 text-sm mt-1">{step.description}</p>
                  {step.criteria && (
                    <div className="mt-2">
                      <p className="text-gray-500 text-[10px] mb-1">What to look for:</p>
                      <ul className="space-y-0.5">{step.criteria.map((c, i) => <li key={i} className="text-xs text-gray-400">• {c}</li>)}</ul>
                    </div>
                  )}
                  {step.actionLink && (
                    <a href={step.actionLink} className="inline-flex items-center gap-1 text-amber-400 text-xs mt-2 hover:text-amber-300">
                      Try this now <ChevronRight className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {guide.afterAction && (
          <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
            <h2 className="text-white font-bold mb-2">{guide.afterAction.title}</h2>
            <p className="text-gray-300 text-sm">{guide.afterAction.content}</p>
          </div>
        )}
      </div>
    );
  }

  // Apply category + search filter together. Search matches title or subtitle.
  const byCategory = category === 'all' ? guides : guides.filter(g => g.category === category);
  const filtered = !search ? byCategory : byCategory.filter(g => {
    const q = search.toLowerCase();
    return (g.title || '').toLowerCase().includes(q) || (g.subtitle || '').toLowerCase().includes(q);
  });

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-blue-500 flex items-center justify-center"><Layers className="w-5 h-5 text-white" /></div>
          Learning Library
        </h1>
        <p className="text-gray-500 text-sm mt-1">Workflow guides that teach you how to combine haythix features for real-world tasks. Help articles teach features. <strong>Library guides teach strategies.</strong></p>
      </div>

      {/* Search input — added so users can find guides by keyword */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search workflow guides..."
          className="w-full bg-dark-800 border border-dark-700 rounded-xl pl-10 pr-4 py-3 text-white text-sm placeholder-gray-600 focus:border-amber-500 focus:outline-none" />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        <button onClick={() => setCategory('all')} className={`px-4 py-2 rounded-xl text-xs font-medium whitespace-nowrap ${category === 'all' ? 'bg-amber-400/15 text-amber-400 border border-amber-400/30' : 'bg-dark-800 text-gray-500 border border-dark-700'}`}>
          All ({guides.length})
        </button>
        {Object.entries(CATEGORY_META).map(([id, meta]) => {
          const count = guides.filter(g => g.category === id).length;
          return (
            <button key={id} onClick={() => setCategory(id)} className={`px-4 py-2 rounded-xl text-xs font-medium whitespace-nowrap ${category === id ? 'bg-amber-400/15 text-amber-400 border border-amber-400/30' : 'bg-dark-800 text-gray-500 border border-dark-700'}`}>
              {meta.icon} {meta.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Load error — show retry button so users know it's not just empty */}
      {loadError && guides.length === 0 && !loading && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-5 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-300">Couldn't load library guides</p>
            <p className="text-xs text-red-300/80 mt-0.5">{loadError}. Search will stay empty until the guides load.</p>
          </div>
          <button onClick={loadGuides} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/30 hover:bg-red-500/10">
            <RefreshCw className="w-3 h-3" /> Retry
          </button>
        </div>
      )}

      {/* No-match state — distinguish from load failure */}
      {!loadError && guides.length > 0 && filtered.length === 0 && (
        <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-8 text-center">
          <Search className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No guides match {search ? `"${search}"` : 'this filter'}</p>
          <p className="text-xs text-gray-600 mt-1">
            {search ? 'Try a different keyword' : 'Switch the category above'} or clear filters to see all {guides.length} guides.
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(g => (
            <button key={g.guideId} onClick={() => setParams({ guide: g.guideId })}
              className="bg-dark-800 rounded-2xl border border-dark-700/50 p-4 text-left hover:border-amber-400/30 transition-all">
              <div className="text-3xl mb-2">{g.iconEmoji}</div>
              <h3 className="text-white text-sm font-bold">{g.title}</h3>
              <p className="text-gray-500 text-xs mt-1 line-clamp-2">{g.subtitle}</p>
              <div className="flex items-center gap-2 mt-3 text-[10px]">
                <span className="text-gray-500 flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {g.estimatedTime}</span>
                <span className={`px-1.5 py-0.5 rounded font-bold ${DIFF_COLORS[g.difficulty] || ''}`}>{g.difficulty}</span>
                <span className="text-gray-500">{g.featuresUsed?.length} features</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
